import { useState, useRef, useEffect } from 'react';
import VimeoPlayer from '@vimeo/player';
import qs from 'query-string';

import sleep from '../../../../../util/sleep';
import getLogger from '../../../../../util/logger';

const logger = getLogger('VideoSlideshowPlayback');

const DEFAULT_RETRY_ATTEMPT_SECONDS = 1;
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Video Slideshow Playback hook.
 *
 * @param {Object} options The options.
 * @param {Array} [options.videos] The collection of videos
 * @param {Object} [options.video] The current active video
 * @param {Number} [options.nextVideoId] The ID of the next video to be displayed
 * @param {function} [options.onChangeVideo] Function to call once the time for the next video to be displayed comes
 * @param {function} [options.onPlayVideo] Function to call once a given video plays
 * @param {function} [options.onLoadedVideo] Function to call once a given video loads
 * @param {boolean} [options.allowAutoPlay] Determines if auto play should be allowed
 * @param {boolean} [options.allowControls] Determines if controls should be allowed
 */
const useVideoSlideshowPlayback = ({
  videos,
  video: currentVideo,
  nextVideoId,
  onChangeVideo,
  onPlayVideo,
  onLoadedVideo,
  allowAutoPlay = true,
  allowControls = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedPercent, setElapsedPercent] = useState(0);
  const [hasBeenSetup, setHasBeenSetup] = useState(false);
  const [retries, setRetries] = useState(MAX_RETRY_ATTEMPTS);
  const videoPlayerRef = useRef();
  const vimeoPlayerRef = useRef();

  const getVideoQueryParams = () => {
    return qs.stringify({
      playsinline: 1,
      badge: 0,
      pip: 0,
      autoplay: allowAutoPlay ? 1 : 0,
      controls: allowControls ? 1 : 0,
    });
  };

  const onSetupVimeoPlayer = () => {
    if (!videoPlayerRef?.current) {
      return;
    }

    if (!vimeoPlayerRef.current) {
      vimeoPlayerRef.current = new VimeoPlayer(videoPlayerRef.current);
    }
    setHasBeenSetup(true);
    const videoUrl = `${currentVideo.embed_url}&${getVideoQueryParams()}`;
    vimeoPlayerRef.current.loadVideo(videoUrl);
    // Reset Events
    vimeoPlayerRef.current.off('play');
    vimeoPlayerRef.current.off('pause');
    vimeoPlayerRef.current.off('ended');
    vimeoPlayerRef.current.off('loaded');
    vimeoPlayerRef.current.off('timeupdate');
    vimeoPlayerRef.current.off('error');
    // Setup Events
    vimeoPlayerRef.current.on('play', () => {
      setIsPlaying(true);
      onPlayVideo?.();
    });
    vimeoPlayerRef.current.on('pause', () => {
      setIsPlaying(false);
    });
    vimeoPlayerRef.current.on('ended', () => {
      if (allowAutoPlay) {
        onChangeVideo({ nextVideoId });
      } else {
        setIsPlaying(false);
      }
    });
    vimeoPlayerRef.current.on('loaded', () => {
      setElapsedPercent(0);
      onLoadedVideo?.();
    });
    vimeoPlayerRef.current.on('timeupdate', ({ percent }) => {
      setElapsedPercent(percent);
    });
    vimeoPlayerRef.current.on('error', e => {
      logger.error('VideoSlideshowPlayback::error', e);
      setIsPlaying(false);
    });
  };

  const onPlaybackStop = async ({ resetElapsedPercent } = {}) => {
    await vimeoPlayerRef?.current?.pause();
    if (resetElapsedPercent) {
      setElapsedPercent(0);
    }
  };

  const onPlaybackStart = async () => {
    // If we have no videos, reset everything and stop
    if ((videos?.length ?? 0) === 0) {
      onPlaybackStop({ resetElapsedPercent: true });
      return;
    }

    try {
      await vimeoPlayerRef?.current?.play();
    } catch (e) {
      // Assuming an error, stop the playback
      onPlaybackStop({ resetElapsedPercent: true });
      setIsPlaying(false);
    }
  };

  const onThumbnailClick = ({ videoId }) => {
    onPlaybackStop({ resetElapsedPercent: true });
    // Ignore the click if we click on the active slide
    if (videoId === currentVideo?.id) {
      return;
    }

    onChangeVideo({ nextVideoId: videoId, isPlaying: false });
  };

  useEffect(() => {
    if (currentVideo && vimeoPlayerRef?.current) {
      onSetupVimeoPlayer();
      if (allowAutoPlay) {
        onPlaybackStart();
      }
    }
  }, [currentVideo, videoPlayerRef?.current, nextVideoId]);

  useEffect(() => {
    const onPrepareVimeoPlayer = async () => {
      if (!hasBeenSetup && retries > 0 && !vimeoPlayerRef?.current) {
        try {
          onSetupVimeoPlayer();
          if (currentVideo && allowAutoPlay) {
            onPlaybackStart();
          }
        } catch {
          // Force retry if possible
          if (retries > 0) {
            await sleep(DEFAULT_RETRY_ATTEMPT_SECONDS * 1000);
            setRetries(prev => prev - 1);
          }
        }
      }
    };

    onPrepareVimeoPlayer();
  }, [videoPlayerRef?.current, retries]);

  return {
    isPlaying,
    elapsedPercent,
    videoPlayerRef,
    vimeoPlayerRef,
    onThumbnailClick,
    onPlaybackStart,
    onPlaybackStop,
  };
};

export default useVideoSlideshowPlayback;
